import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import QuickActions from '../../components/navigation/QuickActions';
import Icon from '../../components/AppIcon';

// Components
import AutomationOverview from './components/AutomationOverview';
import PlatformCard from './components/PlatformCard';
import ActivityChart from './components/ActivityChart';
import AIBilling from './components/AIBilling';
import LiveQueuePanel from './components/LiveQueuePanel';
import RecentActivityFeed from './components/RecentActivityFeed';
import ErrorLogFeed from './components/ErrorLogFeed';
import MidjourneyCard from './components/MidjourneyCard';
import ScheduledWorkloadPanel from './components/ScheduledWorkloadPanel';
import PerformanceInsights from './components/PerformanceInsights';

// Services
import { dashboardService } from '../../services/dashboardService';
import {
  getTodayJobStats,
  getPendingJobs,
  getOldestPendingJobAge,
  getAverageProcessingTime,
  getFailedJobsToday
} from '../../services/automationQueueService';
import { getScheduledWorkload } from '../../services/scheduledWorkloadService';
import { supabase } from '../../lib/supabase';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';

const REFRESH_MS = 30_000;

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');

const relTime = (ts) => {
  if (!ts) return 'Never';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
};

const AutomationControlPanel = () => {
  const { activeProfile } = useLanguageProfile();
  const startDashboardRequest = useRequestGuard();
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [insightsSelection, setInsightsSelection] = useState(null);

  // Platform stats
  const [wpStats, setWpStats]   = useState({ sites: 0, totalArticles: 0, generatedToday: 0, queued: 0 });
  const [pinStats, setPinStats] = useState({ totalPins: 0, publishedToday: 0, scheduled: 0, failed: 0 });
  const [fbStats, setFbStats]   = useState({ pages: 0, publishedToday: 0, scheduled: 0, failed: 0 });
  const [etsyStats, setEtsyStats] = useState({ shops: 0, activeListings: 0, syncedToday: 0, lastSync: null });
  const [instagramStats, setInstagramStats] = useState({ accounts: 0, publishedToday: 0, scheduled: 0, failed: 0 });
  const [threadsStats, setThreadsStats] = useState({ accounts: 0, publishedToday: 0, scheduled: 0, failed: 0 });

  // Queue
  const [jobStats, setJobStats]   = useState({ stats: {} });
  const [pendingJobs, setPendingJobs] = useState([]);
  const [oldestJobAge, setOldestJobAge] = useState(0);
  const [avgProcessingTime, setAvgProcessingTime] = useState(0);

  // Activity & alerts
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);

  // Scheduled workload (posting forecast)
  const [workload, setWorkload] = useState({ pinterest: { accounts: [] }, facebook: { accounts: [] }, threads: { accounts: [] } });

  const loadData = useCallback(async () => {
    if (!supabase) return;

    const isCurrent = startDashboardRequest();
    const profileId = activeProfile?.id || null;

    const results = await Promise.allSettled([
      dashboardService.getWordpressStats(profileId),
      dashboardService.getPinterestStats(profileId),
      dashboardService.getFacebookStats(profileId),
      dashboardService.getEtsyStats(profileId),
      getTodayJobStats(profileId),
      getPendingJobs(15, profileId),
      getOldestPendingJobAge(profileId),
      getAverageProcessingTime(profileId),
      dashboardService.getRecentActivity(25, profileId),
      getFailedJobsToday(5, profileId),
      getScheduledWorkload(profileId),
      dashboardService.getInstagramStats(profileId),
      dashboardService.getThreadsStats(profileId)
    ]);

    if (!isCurrent()) return;

    const get = (i) => results[i].status === 'fulfilled' ? results[i].value : null;

    const wp     = get(0);
    const pin    = get(1);
    const fb     = get(2);
    const etsy   = get(3);
    const jobs   = get(4);
    const pend   = get(5);
    const age    = get(6);
    const avg    = get(7);
    const acts   = get(8);
    const failed = get(9);
    const wkld   = get(10);
    const instagram = get(11);
    const threads = get(12);

    if (wp)   setWpStats(wp);
    if (pin)  setPinStats(pin);
    if (fb)   setFbStats(fb);
    if (etsy) setEtsyStats(etsy);
    if (instagram) setInstagramStats(instagram);
    if (threads) setThreadsStats(threads);
    if (jobs) setJobStats(jobs);
    if (pend?.data) setPendingJobs(pend.data);
    if (age)  setOldestJobAge(age.ageMinutes || 0);
    if (avg)  setAvgProcessingTime(avg.avgSeconds || 0);
    if (acts) setRecentActivity(acts);
    if (wkld) setWorkload(wkld);

    // Build real alerts from data. OpenAI billing and Midjourney connection
    // status are now real, per-user data (synced from their own accounts) —
    // each widget surfaces its own connection/sync state directly rather
    // than feeding a synthetic alert here.
    const baseAlerts = dashboardService.generateAlerts(
      wp   || {},
      pin  || {},
      fb   || {},
      jobs?.stats || {},
      {},
      failed?.data || [],
      instagram || {},
      threads || {}
    );

    setAlerts(baseAlerts);
    setLastRefresh(new Date());
    setLoading(false);
  }, [activeProfile?.id, startDashboardRequest]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(loadData, REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, loadData]);

  return (
    <SidebarProvider>
      <Helmet>
        <title>Automation Control Panel – GymPlix</title>
        <meta name="description" content="Live automation monitoring for WordPress, Pinterest, Facebook, Etsy and AI." />
      </Helmet>

      <div className="min-h-screen bg-[#F8FAFC]">
        <Sidebar />
        <QuickActions />

        <main className="main-content">
          <div className="max-w-[1600px] mx-auto space-y-5">

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-[#0F172A] tracking-tight">Automation Control Panel</h1>
                <p className="text-sm text-[#64748B] mt-0.5">Live monitoring across all platforms</p>
              </div>

              <div className="flex items-center gap-2">
                {lastRefresh && (
                  <span className="text-xs text-[#94A3B8] hidden sm:block">Updated {relTime(lastRefresh)}</span>
                )}
                <button
                  onClick={() => setAutoRefresh(r => !r)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    autoRefresh
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-white border-[#E5E7EB] text-[#64748B] hover:border-[#CBD5E1]'
                  }`}
                >
                  <Icon name="RefreshCw" size={12} className={autoRefresh ? 'animate-spin' : ''} />
                  {autoRefresh ? 'Live' : 'Paused'}
                </button>
                <button
                  onClick={loadData}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-[#E5E7EB] text-[#64748B] hover:border-[#CBD5E1] transition-all"
                >
                  <Icon name="RefreshCw" size={12} />
                  Refresh
                </button>
              </div>
            </div>

            {/* ── AUTOMATION OVERVIEW (replaces 8 small cards) ─────────── */}
            <AutomationOverview
              wpStats={wpStats}
              pinStats={pinStats}
              fbStats={fbStats}
              etsyStats={etsyStats}
              jobStats={jobStats}
              lastRefresh={lastRefresh}
              loading={loading}
            />

            {/* ── PLATFORM CARDS ──────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
              <PlatformCard
                platform="WordPress"
                icon="FileText"
                accentColor="#21759B"
                loading={loading}
                stats={[
                  { label: 'Connected Sites', value: fmt(wpStats.sites) },
                  { label: 'Total Articles', value: fmt(wpStats.totalArticles) },
                  { label: 'Generated Today', value: fmt(wpStats.generatedToday) },
                  { label: 'In Queue', value: fmt(wpStats.queued) }
                ]}
              />
              <PlatformCard
                platform="Pinterest"
                icon="Image"
                accentColor="#E60023"
                loading={loading}
                stats={[
                  { label: 'Total Pins', value: fmt(pinStats.totalPins) },
                  { label: 'Published Today', value: fmt(pinStats.publishedToday) },
                  { label: 'Scheduled', value: fmt(pinStats.scheduled) },
                  { label: 'Failed', value: fmt(pinStats.failed) }
                ]}
                badge={pinStats.failed > 0 ? { label: `${pinStats.failed} failed`, color: '#EF4444', bg: '#FEE2E2' } : null}
              />
              <PlatformCard
                platform="Facebook"
                icon="Users"
                accentColor="#1877F2"
                loading={loading}
                stats={[
                  { label: 'Connected Pages', value: fmt(fbStats.pages) },
                  { label: 'Published Today', value: fmt(fbStats.publishedToday) },
                  { label: 'Scheduled Posts', value: fmt(fbStats.scheduled) },
                  { label: 'Failed Posts', value: fmt(fbStats.failed) }
                ]}
                badge={fbStats.pages === 0 ? { label: 'Not connected', color: '#94A3B8', bg: '#F1F5F9' } : null}
              />
              <PlatformCard
                platform="Etsy"
                icon="ShoppingBag"
                accentColor="#F56400"
                loading={loading}
                stats={[
                  { label: 'Connected Shops', value: fmt(etsyStats.shops) },
                  { label: 'Active Listings', value: fmt(etsyStats.activeListings) },
                  { label: 'Synced Today', value: fmt(etsyStats.syncedToday) },
                  { label: 'Last Sync', value: etsyStats.lastSync ? relTime(etsyStats.lastSync) : null }
                ]}
                badge={etsyStats.shops === 0 ? { label: 'Not connected', color: '#94A3B8', bg: '#F1F5F9' } : null}
              />
              <PlatformCard
                platform="Instagram"
                icon="Instagram"
                accentColor="#C13584"
                loading={loading}
                stats={[
                  { label: 'Connected Accounts', value: fmt(instagramStats.accounts) },
                  { label: 'Published Today', value: fmt(instagramStats.publishedToday) },
                  { label: 'Scheduled Posts', value: fmt(instagramStats.scheduled) },
                  { label: 'Failed Posts', value: fmt(instagramStats.failed) }
                ]}
                badge={instagramStats.accounts === 0 ? { label: 'Not connected', color: '#94A3B8', bg: '#F1F5F9' } : null}
              />
              <PlatformCard
                platform="Threads"
                icon="AtSign"
                accentColor="#111827"
                loading={loading}
                stats={[
                  { label: 'Connected Accounts', value: fmt(threadsStats.accounts) },
                  { label: 'Published Today', value: fmt(threadsStats.publishedToday) },
                  { label: 'Scheduled Posts', value: fmt(threadsStats.scheduled) },
                  { label: 'Failed Posts', value: fmt(threadsStats.failed) }
                ]}
                badge={threadsStats.accounts === 0 ? { label: 'Not connected', color: '#94A3B8', bg: '#F1F5F9' } : null}
              />
            </div>

            {/* ── CHART + AI BILLING ───────────────────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2" style={{ minHeight: 360 }}>
                <ActivityChart />
              </div>
              <AIBilling />
            </div>

            <PerformanceInsights selection={insightsSelection} />

            {/* ── QUEUE + RECENT ACTIVITY + ALERTS / MIDJOURNEY ─────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              {/* Queue */}
              <LiveQueuePanel
                stats={jobStats.stats || {}}
                pendingJobs={pendingJobs}
                oldestJobAge={oldestJobAge}
                avgProcessingTime={avgProcessingTime}
                loading={loading}
              />

              {/* Recent Activity */}
              <RecentActivityFeed activities={recentActivity} loading={loading} />

              {/* Alerts + Midjourney stacked */}
              <div className="flex flex-col gap-4">
                <ErrorLogFeed alerts={alerts} loading={loading} />
                <MidjourneyCard />
              </div>
            </div>

            {/* ── SCHEDULED WORKLOAD / POSTING FORECAST ──────────────────── */}
            <ScheduledWorkloadPanel
              data={workload}
              loading={loading}
              onViewInsights={(platform, accountId) => {
                setInsightsSelection({ platform, accountId, requestedAt: Date.now() });
                requestAnimationFrame(() => document.getElementById('performance-insights')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
              }}
            />

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AutomationControlPanel;
